/**
 * ESC/POS for thermal printers, as a picture.
 *
 * Most low-cost 58/80 mm printers have no Arabic in their character sets,
 * and those that do shape it wrongly. So the receipt is drawn on a canvas
 * by the phone -- which shapes Arabic properly and draws the QR -- turned
 * into black and white dots, and sent as a raster image (GS v 0). Every
 * ESC/POS printer understands that.
 */

/** Printable dots across: 80 mm paper = 576 dots, 58 mm = 384 (203 dpi). */
export const DOTS = { 80: 576, 58: 384 } as const
export type Paper = keyof typeof DOTS

/** A 1-bit image, rows of bytes, most significant bit = leftmost dot. */
export interface Bitmap { width: number; height: number; bytesPerRow: number; data: Uint8Array }

/** Grey to black/white by a fixed threshold: crisp text, which is what a
 *  receipt is. (Dithering helps photos and blurs letters.) */
export function toBitmap(rgba: Uint8ClampedArray, width: number, height: number,
                         threshold = 160): Bitmap {
  const bytesPerRow = Math.ceil(width / 8)
  const data = new Uint8Array(bytesPerRow * height)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      const a = rgba[i + 3] / 255
      // Transparent counts as white paper.
      const lum = (0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2]) * a + 255 * (1 - a)
      if (lum < threshold) data[y * bytesPerRow + (x >> 3)] |= 0x80 >> (x & 7)
    }
  }
  return { width, height, bytesPerRow, data }
}

/** Drop blank rows at the bottom: paper costs money. */
export function trim(b: Bitmap): Bitmap {
  let h = b.height
  while (h > 1) {
    const row = b.data.subarray((h - 1) * b.bytesPerRow, h * b.bytesPerRow)
    if (row.some((v) => v !== 0)) break
    h--
  }
  return { ...b, height: h, data: b.data.slice(0, h * b.bytesPerRow) }
}

const ESC = 0x1b
const GS = 0x1d

/**
 * The whole job: reset, the image in bands (many printers' buffers take
 * no more than a few hundred rows per command), feed, cut.
 */
export function rasterJob(b: Bitmap, { band = 128, feed = 4, cut = true } = {}): Uint8Array {
  const parts: number[][] = [[ESC, 0x40]]                     // ESC @ initialise
  for (let y = 0; y < b.height; y += band) {
    const h = Math.min(band, b.height - y)
    const xL = b.bytesPerRow & 0xff
    const xH = (b.bytesPerRow >> 8) & 0xff
    // GS v 0 m xL xH yL yH d1...dk
    parts.push([GS, 0x76, 0x30, 0x00, xL, xH, h & 0xff, (h >> 8) & 0xff])
    parts.push(Array.from(b.data.subarray(y * b.bytesPerRow, (y + h) * b.bytesPerRow)))
  }
  parts.push([ESC, 0x64, feed])                               // ESC d n  feed n lines
  if (cut) parts.push([GS, 0x56, 0x42, 0x00])                 // GS V B 0  partial cut
  const total = parts.reduce((n, p) => n + p.length, 0)
  const out = new Uint8Array(total)
  let o = 0
  for (const p of parts) { out.set(p, o); o += p.length }
  return out
}
